import type { Cookie, CookieSetDetails } from ".";

export type ClientIntegrationType =
  | "poshmark"
  | "mercari"
  | "vinted"
  | "depop"
  | "what-not";

type CookieAttrConfig = {
  domain: string;
  path?: string;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: "lax" | "strict" | "no_restriction";
};

const MercariCookieConfig: Record<string, CookieAttrConfig> = {
  _mwus: { domain: "www.mercari.com", path: "/", httpOnly: true },
  "_mwus.sig": { domain: "www.mercari.com", path: "/", httpOnly: true },
};

const WhatnotCookieConfig: Record<string, CookieAttrConfig> = {
  FPC: { domain: "www.whatnot.com", path: "/" },
  KP_UIDz: {
    domain: "www.whatnot.com",
    path: "/",
    httpOnly: true,
    secure: true,
  },
  "KP_UIDz-ssn": {
    domain: "www.whatnot.com",
    path: "/",
    httpOnly: true,
    secure: true,
    sameSite: "no_restriction",
  },
  "__Secure-access-token": {
    domain: "www.whatnot.com",
    path: "/",
    httpOnly: true,
    secure: true,
    sameSite: "lax",
  },
  "__Secure-access-token-fp": {
    domain: ".whatnot.com",
    path: "/",
    httpOnly: true,
    secure: true,
    sameSite: "lax",
  },
  "__Secure-access-token-expiration": {
    domain: "www.whatnot.com",
    path: "/",
    secure: true,
    sameSite: "lax",
  },
  "__Secure-claims": {
    domain: "www.whatnot.com",
    path: "/",
    secure: true,
    sameSite: "lax",
  },
  "__Secure-is-http-only-auth": {
    domain: "www.whatnot.com",
    path: "/",
    secure: true,
    sameSite: "lax",
  },
  "__Secure-refresh-token": {
    domain: "www.whatnot.com",
    path: "/",
    httpOnly: true,
    secure: true,
    sameSite: "lax",
  },
  "__Secure-refresh-token-fp": {
    domain: ".whatnot.com",
    path: "/",
    httpOnly: true,
    secure: true,
    sameSite: "lax",
  },
  "__Secure-urs": {
    domain: "www.whatnot.com",
    path: "/",
    secure: true,
    sameSite: "lax",
  },
  __spdt: { domain: "www.whatnot.com", path: "/", sameSite: "strict" },
  __stripe_mid: {
    domain: ".www.whatnot.com",
    path: "/",
    secure: true,
    sameSite: "strict",
  },
  __stripe_sid: {
    domain: ".www.whatnot.com",
    path: "/",
    secure: true,
    sameSite: "strict",
  },
  _dd_s: { domain: "www.whatnot.com", path: "/", sameSite: "strict" },
  cas_cid: {
    domain: "www.whatnot.com",
    path: "/",
    httpOnly: true,
    secure: true,
    sameSite: "lax",
  },
  device: { domain: "www.whatnot.com", path: "/" },
  f3k2kqs7xc: {
    domain: "www.whatnot.com",
    path: "/",
    secure: true,
    sameSite: "strict",
  },
  g_state: { domain: "www.whatnot.com", path: "/" },
  "stable-id": {
    domain: "www.whatnot.com",
    path: "/",
    secure: true,
    sameSite: "lax",
  },
  "tatari-cookie-test": { domain: "www.whatnot.com", path: "/" },
  usid: {
    domain: "www.whatnot.com",
    path: "/",
    secure: true,
    sameSite: "lax",
  },
};

const DepopCookieConfig: Record<string, CookieAttrConfig> = {
  ACTIVITY_TRACKER_PERSISTENT_SESSION_ID: {
    domain: "www.depop.com",
    path: "/",
  },
  ATPlatformType: { domain: "www.depop.com", path: "/" },
  NEXT_LOCALE: { domain: "www.depop.com", path: "/" },
  __rmid: { domain: "www.depop.com", path: "/", secure: true, sameSite: "lax" },
  __stripe_mid: {
    domain: ".www.depop.com",
    path: "/",
    secure: true,
    sameSite: "strict",
  },
  _cfuvid: {
    domain: ".www.depop.com",
    path: "/",
    httpOnly: true,
    secure: true,
    sameSite: "no_restriction",
  },
  _dd_s: { domain: "www.depop.com", path: "/", sameSite: "strict" },
  _dd_s_v2: {
    domain: "www.depop.com",
    path: "/",
    secure: true,
    sameSite: "strict",
  },
  access_token: { domain: "www.depop.com", path: "/", secure: true },
  external_id: { domain: "www.depop.com", path: "/" },
  hashed_external_id: { domain: "www.depop.com", path: "/" },
  g_state: { domain: "www.depop.com", path: "/" },
  hashed_user_email: { domain: "www.depop.com", path: "/" },
  language: { domain: "www.depop.com", path: "/" },
  user_id: { domain: "www.depop.com", path: "/" },
};

const PoshmarkJwtConfig: CookieAttrConfig = {
  domain: "poshmark.com",
  path: "/",
  httpOnly: true,
  secure: true,
  sameSite: "lax",
};

const PlatformCookieConfigs: Partial<
  Record<ClientIntegrationType, Record<string, CookieAttrConfig>>
> = {
  mercari: MercariCookieConfig,
  "what-not": WhatnotCookieConfig,
  depop: DepopCookieConfig,
};

function cookieUrl(domain: string, path = "/"): string {
  const host = domain.replace(/^\./, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `https://${host}${normalizedPath}`;
}

function isDomainCookie(domain: string): boolean {
  return domain.startsWith(".");
}

function mapStoredSameSite(
  sameSite?: Cookie["sameSite"] | string
): chrome.cookies.SameSiteStatus | undefined {
  if (sameSite == null) return undefined;
  const normalized = String(sameSite).toLowerCase();
  if (normalized === "no_restriction" || normalized === "none")
    return "no_restriction";
  if (normalized === "lax") return "lax";
  if (normalized === "strict") return "strict";
  if (normalized === "unspecified") return "unspecified";
  return undefined;
}

function fromAttrConfig(
  name: string,
  value: string,
  config: CookieAttrConfig
): CookieSetDetails {
  const path = config.path || "/";
  // chrome.cookies.set: omit `domain` for host-only cookies. Passing a domain
  // (even without a leading ".") creates a domain cookie, which Chrome stores
  // / displays as ".www.example.com".
  const details: CookieSetDetails = {
    url: cookieUrl(config.domain, path),
    name,
    value,
    path,
  };
  if (isDomainCookie(config.domain)) {
    details.domain = config.domain;
  }
  if (config.secure != null) details.secure = config.secure;
  if (config.httpOnly != null) details.httpOnly = config.httpOnly;
  if (config.sameSite != null) details.sameSite = config.sameSite;
  return details;
}

function fromStoredCookie(
  cookie: Cookie,
  fallbackDomain = ".example.com"
): CookieSetDetails | null {
  if (!cookie?.name || cookie.value == null || cookie.value === "") return null;
  const domain = cookie.domain || fallbackDomain;
  const path = cookie.path || "/";
  const details: CookieSetDetails = {
    url: cookieUrl(domain, path),
    name: cookie.name,
    value: cookie.value,
    path,
  };
  // Prefer stored hostOnly; otherwise treat a leading "." as a domain cookie.
  const hostOnly =
    cookie.hostOnly != null ? cookie.hostOnly : !isDomainCookie(domain);
  if (!hostOnly) {
    details.domain = domain.startsWith(".") ? domain : `.${domain}`;
  }
  if (cookie.secure != null) details.secure = cookie.secure;
  if (cookie.httpOnly != null) details.httpOnly = cookie.httpOnly;
  if (cookie.expirationDate != null)
    details.expirationDate = cookie.expirationDate;
  const sameSite = mapStoredSameSite(cookie.sameSite);
  if (sameSite != null) details.sameSite = sameSite;
  return details;
}

function fromCookieArray(
  cookies: unknown,
  fallbackDomain: string
): CookieSetDetails[] {
  if (!Array.isArray(cookies)) return [];
  return cookies
    .map((c) => fromStoredCookie(c as Cookie, fallbackDomain))
    .filter((c): c is CookieSetDetails => c != null);
}

function fromCookieMap(
  platform: ClientIntegrationType,
  cookies: Record<string, string>
): CookieSetDetails[] {
  const configMap = PlatformCookieConfigs[platform];
  if (!configMap) return [];

  const details: CookieSetDetails[] = [];
  for (const [name, value] of Object.entries(cookies)) {
    if (value == null || value === "") continue;
    const config = configMap[name];
    if (!config) {
      console.warn(
        `[impersonateCookies] Ignoring unknown ${platform} cookie "${name}"`
      );
      continue;
    }
    details.push(fromAttrConfig(name, value, config));
  }
  return details;
}

const PlatformFallbackDomain: Record<ClientIntegrationType, string> = {
  poshmark: ".poshmark.com",
  mercari: ".mercari.com",
  vinted: ".vinted.com",
  depop: ".depop.com",
  "what-not": ".whatnot.com",
};

export function buildImpersonationCookies(
  platform: ClientIntegrationType,
  accessToken: { cookies?: unknown; jwt?: string }
): CookieSetDetails[] {
  const cookies = accessToken.cookies;

  if (Array.isArray(cookies)) {
    const details = fromCookieArray(cookies, PlatformFallbackDomain[platform]);
    if (details.length === 0) {
      console.warn(
        `[impersonateCookies] ${platform} cookie array empty after filter; nothing to set`
      );
    }
    return details;
  }

  // Legacy Poshmark: jwt string only
  if (platform === "poshmark") {
    if (!accessToken.jwt) {
      console.warn(
        "[impersonateCookies] Poshmark access token missing jwt; nothing to set"
      );
      return [];
    }
    return [fromAttrConfig("jwt", accessToken.jwt, PoshmarkJwtConfig)];
  }

  // Legacy mercari / depop / what-not: name → value map
  if (!cookies || typeof cookies !== "object") {
    console.warn(
      `[impersonateCookies] ${platform} access token cookies missing or invalid; nothing to set`
    );
    return [];
  }

  return fromCookieMap(platform, cookies as Record<string, string>);
}

export function isClientIntegrationType(
  type: string
): type is ClientIntegrationType {
  return (
    type === "poshmark" ||
    type === "mercari" ||
    type === "vinted" ||
    type === "depop" ||
    type === "what-not"
  );
}

/** Chrome cookie / JWT exp are usually seconds; ms values are ~1e12+. */
function toEpochSeconds(value: number): number {
  return value > 1e12 ? value / 1000 : value;
}

function isEpochOlderThanNow(epoch: number): boolean {
  return toEpochSeconds(epoch) < Date.now() / 1000;
}

function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const payload = JSON.parse(atob(segment));
    if (!payload || typeof payload !== "object") return null;
    return payload;
  } catch (_) {
    return null;
  }
}

function isJwtExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (payload?.exp == null || typeof payload.exp !== "number") return true;
  return isEpochOlderThanNow(payload.exp);
}

function getCookieValueFromStored(
  cookies: unknown,
  name: string
): string | undefined {
  if (Array.isArray(cookies)) {
    return (cookies as Cookie[]).find((c) => c?.name === name)?.value;
  }
  if (cookies && typeof cookies === "object") {
    const value = (cookies as Record<string, string>)[name];
    return value != null && value !== "" ? value : undefined;
  }
  return undefined;
}

function areVintedCredentialsExpired(cookies: unknown): boolean {
  if (!Array.isArray(cookies)) return true;
  const refresh = (cookies as Cookie[]).find(
    (c) => c?.name === "refresh_token_web"
  );
  if (!refresh?.value) return true;
  if (
    refresh.expirationDate != null &&
    typeof refresh.expirationDate === "number" &&
    isEpochOlderThanNow(refresh.expirationDate)
  ) {
    return true;
  }
  return isJwtExpired(refresh.value);
}

function areWhatnotCredentialsExpired(cookies: unknown): boolean {
  if (Array.isArray(cookies)) {
    const refresh = (cookies as Cookie[]).find(
      (c) => c?.name === "__Secure-refresh-token"
    );
    if (!refresh?.value) return true;
    // Prefer JWT exp when the refresh token is a JWT; otherwise use Chrome cookie expiry.
    if (decodeJwtPayload(refresh.value)) {
      return isJwtExpired(refresh.value);
    }
    if (
      refresh.expirationDate != null &&
      typeof refresh.expirationDate === "number"
    ) {
      return isEpochOlderThanNow(refresh.expirationDate);
    }
    return false;
  }

  // Legacy name → value map: only check JWT exp; opaque tokens are treated as not expired.
  const refresh = getCookieValueFromStored(cookies, "__Secure-refresh-token");
  if (!refresh) return true;
  if (decodeJwtPayload(refresh)) {
    return isJwtExpired(refresh);
  }
  return false;
}

function arePoshmarkCredentialsExpired(accessToken: {
  cookies?: unknown;
  jwt?: string;
}): boolean {
  // Poshmark jwt payloads do not include `exp`. Prefer Chrome cookie expiry when
  // we have a cookie list; legacy jwt-only tokens are treated as not expired.
  if (Array.isArray(accessToken.cookies)) {
    const jwtCookie = (accessToken.cookies as Cookie[]).find(
      (c) => c?.name === "jwt"
    );
    if (!jwtCookie?.value) return true;
    if (
      jwtCookie.expirationDate != null &&
      typeof jwtCookie.expirationDate === "number"
    ) {
      return isEpochOlderThanNow(jwtCookie.expirationDate);
    }
    return false;
  }

  if (!accessToken.jwt && !getCookieValueFromStored(accessToken.cookies, "jwt"))
    return true;
  return false;
}

/**
 * Returns true when we can tell credentials are expired.
 * Platforms we cannot check (depop, mercari) always return false.
 */
export function areCredentialsExpired(
  platform: ClientIntegrationType,
  accessToken: { cookies?: unknown; jwt?: string }
): boolean {
  if (platform === "vinted") {
    return areVintedCredentialsExpired(accessToken.cookies);
  }
  if (platform === "what-not") {
    return areWhatnotCredentialsExpired(accessToken.cookies);
  }
  if (platform === "poshmark") {
    return arePoshmarkCredentialsExpired(accessToken);
  }
  return false;
}
